// The paper: a texture of fine grain (red channel) and slow, uneven wash (green), made once at load,
// and a full-screen layer that lays it under everything.

import {
  DataTexture,
  GLSL3,
  LinearFilter,
  Mesh,
  PlaneGeometry,
  RawShaderMaterial,
  RepeatWrapping,
  RGBAFormat,
} from "three";
import { EDITION, MAPPING } from "./glsl.ts";
import type { SharedUniforms } from "./stage.ts";

export const PAPER_SIZE = 512;

function fbm(x: number, y: number, seed: number): number {
  const h = (i: number, j: number) => {
    const s = Math.sin(i * 127.1 + j * 311.7 + seed * 74.7) * 43758.5453;
    return s - Math.floor(s);
  };
  const noise = (px: number, py: number, period: number) => {
    const i = Math.floor(px),
      j = Math.floor(py);
    const fx = px - i,
      fy = py - j;
    const sx = fx * fx * (3 - 2 * fx),
      sy = fy * fy * (3 - 2 * fy);
    const w = (a: number) => ((a % period) + period) % period;
    const a = h(w(i), w(j)),
      b = h(w(i + 1), w(j)),
      c = h(w(i), w(j + 1)),
      d = h(w(i + 1), w(j + 1));
    return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
  };
  let v = 0,
    amp = 0.5,
    f = 4;
  for (let o = 0; o < 4; o++) {
    v += amp * noise((x * f) / PAPER_SIZE, (y * f) / PAPER_SIZE, f);
    amp *= 0.5;
    f *= 2;
  }
  return v;
}

/** Tiles seamlessly: the wash's noise wraps at the texture's edge. */
export function paperTexture(): DataTexture {
  const n = PAPER_SIZE;
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const i = (y * n + x) * 4;
      data[i] = Math.floor(Math.random() * Math.random() * 255);
      data[i + 1] = Math.floor(Math.min(1, Math.max(0, fbm(x, y, 3) * 1.3 - 0.15)) * 255);
      data[i + 2] = Math.floor(Math.min(1, Math.max(0, fbm(x, y, 9))) * 255);
      data[i + 3] = 255;
    }
  }
  const tex = new DataTexture(data, n, n, RGBAFormat);
  tex.wrapS = tex.wrapT = RepeatWrapping;
  tex.magFilter = tex.minFilter = LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

export const PAPER_SAMPLE = /* glsl */ `
uniform sampler2D uPaperTex;
// r: grain, g: wash, b: a second, slower wash. Sampled in screen space, like light on the sheet.
vec3 paperAt(vec2 px) {
  vec3 fine = texture(uPaperTex, px / ${PAPER_SIZE.toFixed(1)}).rgb;
  vec3 slow = texture(uPaperTex, px / ${(PAPER_SIZE * 3.7).toFixed(1)}).rgb;
  return vec3(fine.r, slow.g, slow.b);
}
`;

export function paperLayer(shared: SharedUniforms, texture: DataTexture): Mesh {
  const material = new RawShaderMaterial({
    glslVersion: GLSL3,
    uniforms: { ...shared, uPaperTex: { value: texture } },
    depthTest: false,
    depthWrite: false,
    vertexShader: /* glsl */ `
      precision highp float;
      in vec3 position;
      uniform vec2 uViewport;
      out vec2 vPx;
      void main() {
        vPx = (position.xy * 0.5 + 0.5) * uViewport;
        vPx.y = uViewport.y - vPx.y;
        gl_Position = vec4(position.xy, 0.0, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      precision highp float;
      ${MAPPING}
      ${EDITION}
      ${PAPER_SAMPLE}
      in vec2 vPx;
      out vec4 fragColor;
      void main() {
        vec3 p = paperAt(vPx);
        vec3 c = paperTone();
        // Day: warm, uneven parchment. Night: a faint glow behind dark glass.
        vec3 day = c * (1.0 - 0.05 * p.g) * (1.0 - 0.09 * p.r) * (1.0 + 0.03 * p.b);
        vec3 night = c * (1.0 + 0.35 * p.g) + 0.01 * p.r;
        vec3 col = mix(day, night, uNight);
        // The grain catches the light that follows the pointer.
        vec2 e = vec2(1.5, 0.0);
        vec2 n = vec2(paperAt(vPx + e.xy).r - paperAt(vPx - e.xy).r, paperAt(vPx + e.yx).r - paperAt(vPx - e.yx).r) * 0.9;
        col *= 1.0 + mix(0.12, 0.6, uNight) * raking(vPx, n);
        // The scroll's rolled ends, inside the plot only.
        if (vPx.y > uPlot.y && vPx.x > uPlot.x) {
          float r = rollDepth(vPx.x);
          col *= 1.0 - mix(0.07, 0.18, uNight) * r * r;
        }
        vec2 q = vPx / uViewport - vec2(0.5, 0.42);
        col *= 1.0 - mix(0.05, 0.22, uNight) * smoothstep(0.5, 1.05, length(q * vec2(1.0, 1.15)));
        fragColor = vec4(col, 1.0);
      }`,
  });
  const mesh = new Mesh(new PlaneGeometry(2, 2), material);
  mesh.frustumCulled = false;
  mesh.renderOrder = -100;
  return mesh;
}
